import React, { useState, useEffect, useCallback } from 'react';
import { FiTag, FiCheck } from 'react-icons/fi';
import { Modal } from 'components/basicos';
import API from 'shared/api';
import { fmtMonto } from 'shared/format';

// Personaliza un combo que YA esta en el pedido. Usa las etiquetas_disponibles
// (con flag `incluida`) guardadas en cada componente para saber que es gratis y
// que es extra. Si un combo legacy no las trae, las carga del endpoint.
const ComboEdicionModal = ({ open, onClose, producto, onGuardar }) => {
  const [componentes, setComponentes] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!open || !producto) return;
    const comps = (producto.componentes || []).map((c) => ({
      ...c,
      etiquetas: Array.isArray(c.etiquetas) ? c.etiquetas : [],
      etiquetas_disponibles: Array.isArray(c.etiquetas_disponibles) ? c.etiquetas_disponibles : null,
    }));
    const faltan = comps.some((c) => !c.etiquetas_disponibles);
    setComponentes(comps);
    if (!faltan) {
      setCargando(false);
      return;
    }
    setCargando(true);
    Promise.all(
      comps.map((c) =>
        API.get(`/pedidos/producto/${encodeURIComponent(c.codigo_producto)}/etiquetas`)
          .then((r) => ({ codigo: c.codigo_producto, data: r.data.status === 'success' ? (r.data.data || []) : [] }))
          .catch(() => ({ codigo: c.codigo_producto, data: [] }))
      )
    )
      .then((res) => {
        const map = {};
        res.forEach((r) => { map[r.codigo] = r.data; });
        setComponentes((prev) =>
          prev.map((c) => ({
            ...c,
            etiquetas_disponibles: c.etiquetas_disponibles || map[c.codigo_producto] || [],
          }))
        );
      })
      .finally(() => setCargando(false));
  }, [open, producto]);

  const toggleEtiqueta = useCallback((ci, et) => {
    setComponentes((prev) =>
      prev.map((c, i) => {
        if (i !== ci) return c;
        const actual = c.etiquetas || [];
        const ya = actual.some((e) => e.id === et.id);
        const next = ya
          ? actual.filter((e) => e.id !== et.id)
          : [...actual, { id: et.id, nombre: et.nombre, precio: et.precio || 0 }];
        return { ...c, etiquetas: next };
      })
    );
  }, []);

  const calcularExtra = (comps) => {
    let extra = 0;
    comps.forEach((c) => {
      const disp = c.etiquetas_disponibles || [];
      const selIds = (c.etiquetas || []).map((e) => e.id);
      disp.forEach((et) => {
        if (selIds.includes(et.id) && !et.incluida) extra += et.precio || 0;
      });
    });
    return extra;
  };

  const guardar = () => {
    if (!onGuardar) return;
    onGuardar(
      componentes.map((c) => ({
        codigo_producto: c.codigo_producto,
        nombre: c.nombre,
        etiquetas: c.etiquetas || [],
        etiquetas_disponibles: c.etiquetas_disponibles || [],
      })),
      calcularExtra(componentes)
    );
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Personalizar combo" width="lg">
      {cargando ? (
        <div style={{ textAlign: 'center', padding: 20, fontSize: 13, color: 'var(--agx-muted)' }}>
          Cargando personalizaciones...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--agx-text)', marginBottom: '4px' }}>
            {producto?.nombre || 'Combo'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--agx-muted)', marginBottom: '12px' }}>
            Ajusta las personalizaciones de cada componente
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '48vh', overflowY: 'auto' }}>
            {componentes.map((c, ci) => (
              <div key={ci} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--agx-border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-text)' }}>{c.nombre}</div>
                {(c.etiquetas_disponibles || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                    {c.etiquetas_disponibles.map((et) => {
                      const sel = (c.etiquetas || []).some((e) => e.id === et.id);
                      return (
                        <button
                          key={et.id}
                          onClick={() => toggleEtiqueta(ci, et)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 9px',
                            borderRadius: '999px',
                            cursor: 'pointer',
                            border: sel ? '2px solid var(--agx-pri2)' : '1px solid var(--agx-border)',
                            background: sel ? 'var(--agx-pri2)' : 'var(--agx-surface)',
                            color: sel ? '#fff' : 'var(--agx-text)',
                            fontSize: 11,
                            fontWeight: sel ? 700 : 500,
                            fontFamily: 'inherit',
                          }}
                        >
                          {sel && <FiCheck size={11} />}
                          {et.nombre}
                          {et.precio > 0 && (
                            <span style={{ marginLeft: 4, fontWeight: 700, opacity: 0.9 }}>
                              {et.incluida ? 'incluido' : `+$${fmtMonto(et.precio)}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={guardar}
            style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: 'var(--agx-pri2)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <FiTag size={16} /> Guardar personalizaciones
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ComboEdicionModal;
